import { useCallback, useEffect, useMemo, useState } from 'react'
import { ApiError, api, can, refusalReason, shortDate, type Me } from '../../lib/api'
import {
  exceptionsApi,
  SEVERITIES,
  severityTone,
  type AuthorityDocument,
  type AuthorityVersion,
  type Decider,
} from '../../lib/exceptions'
import { ErrorBox, Loading } from '../../components/DataTable'
import { Status, Toggletip } from '../../components/kit'
import { personLabel, usePeopleNames } from '../../lib/people'
import '../../styles/prose.css'
import '../../styles/exceptions.css'

// Who may approve a due diligence exception at each severity (decision 2 of
// the wave 3 plan): Low the TPRM lead, Medium the risk officer, High the risk
// officer and the CISO, Critical the executive after the risk officer.
//
// It is the approval matrix's shape, beside it in Settings: one person drafts
// a change, a second person holding the authority to decide a risk position
// puts it in force, and a version in force is never edited. An exception
// already sent for review is decided under the version it was sent under.

function asApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))
}

function clone(a: AuthorityDocument): AuthorityDocument {
  return JSON.parse(JSON.stringify(a)) as AuthorityDocument
}

function AuthorityTable({ doc, deciders }: { doc: AuthorityDocument; deciders: Decider[] }) {
  const name = (code: string) => deciders.find((d) => d.code === code)?.name ?? code
  return (
    <div className="table-wrap">
      <table className="dd-table">
        <caption className="vh">Who approves at each severity</caption>
        <thead>
          <tr>
            <th scope="col">Severity</th>
            <th scope="col">Approved by</th>
            <th scope="col">Order</th>
          </tr>
        </thead>
        <tbody>
          {SEVERITIES.map((s) => {
            const lv = doc.levels.find((l) => l.severity === s.code)
            return (
              <tr key={s.code}>
                <th scope="row">
                  <Status tone={severityTone(s.code)}>{s.name}</Status>
                </th>
                <td>{lv ? lv.steps.map((st) => name(st.persona)).join(lv.inOrder ? ', then ' : ' and ') : 'Nobody'}</td>
                <td className="prose-sub">{lv && lv.steps.length > 1 ? (lv.inOrder ? 'In this order' : 'Either order, both') : 'One approval'}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export function ExceptionAuthoritySettings() {
  const [versions, setVersions] = useState<AuthorityVersion[] | null>(null)
  const [deciders, setDeciders] = useState<Decider[]>([])
  const [me, setMe] = useState<Me | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [draft, setDraft] = useState<AuthorityDocument | null>(null)
  const [dirty, setDirty] = useState(false)
  const [busy, setBusy] = useState(false)
  const [actError, setActError] = useState<ApiError | null>(null)
  const [problems, setProblems] = useState<string[]>([])
  const [note, setNote] = useState('')
  const names = usePeopleNames()

  const load = useCallback(() => {
    exceptionsApi
      .authorityVersions()
      .then((r) => {
        setVersions(r.versions)
        setDeciders(r.deciders)
        const open = r.versions.find((v) => v.status === 'DRAFT')
        setDraft(open?.authority ? clone(open.authority) : null)
        setDirty(false)
        setError(null)
      })
      .catch(setError)
  }, [])
  useEffect(load, [load])
  useEffect(() => {
    api.me().then(setMe).catch(() => setMe(null))
  }, [])

  const active = useMemo(() => versions?.find((v) => v.status === 'ACTIVE') ?? null, [versions])
  const openDraft = useMemo(() => versions?.find((v) => v.status === 'DRAFT') ?? null, [versions])
  const choosable = deciders.filter((d) => d.canDecide)
  const canWrite = can('programme.write')
  const canPromote = can('risk.approve')
  const drewIt = openDraft != null && me != null && openDraft.createdBy.toLowerCase() === me.subject.toLowerCase()

  function run(p: Promise<unknown>, after: (v: unknown) => void) {
    setBusy(true)
    setActError(null)
    setProblems([])
    p.then(after)
      .catch((err) => {
        const ae = asApiError(err)
        const body = ae.body as { problems?: string[] } | null
        if (body?.problems?.length) setProblems(body.problems)
        else setActError(ae)
      })
      .finally(() => setBusy(false))
  }

  function startDraft() {
    run(exceptionsApi.draftAuthority(undefined, 'Drafted from the version in force'), () => {
      setNote('A draft is open. Change it, save it, and ask a second person to put it in force.')
      load()
    })
  }

  function saveDraft() {
    if (!openDraft || !draft) return
    run(exceptionsApi.saveAuthorityDraft(openDraft.id, draft), (r) => {
      const res = r as { problems: string[] }
      setDirty(false)
      setProblems(res.problems ?? [])
      setNote(res.problems?.length ? '' : 'Draft saved.')
      load()
    })
  }

  function promote() {
    if (!openDraft) return
    run(exceptionsApi.promoteAuthority(openDraft.id), (r) => {
      setNote((r as { note: string }).note || 'In force from now.')
      load()
    })
  }

  function setLevel(sev: string, patch: (l: AuthorityDocument['levels'][number]) => void) {
    setDraft((d) => {
      if (!d) return d
      const next = clone(d)
      const lv = next.levels.find((l) => l.severity === sev)
      if (lv) patch(lv)
      return next
    })
    setDirty(true)
    setNote('')
  }

  if (error) return <ErrorBox error={error} what="Who can accept risks" />
  if (!versions) return <Loading what="who can accept risks" />

  return (
    <div className="prose-page dd-page">
      <div className="prose-head">
        <div className="prose-head-main">
          <div className="prose-title-row">
            <h1>Who can accept risks</h1>
            <Toggletip label="About who can accept risks">
              Who may approve an exception at each severity. One person drafts a change and a second person puts it in
              force. The person who raised an exception never approves it.
            </Toggletip>
          </div>
          <p className="prose-meta">
            {active && (
              <span>
                Version {active.versionNo} in force{active.activatedAt ? ' since ' + shortDate(active.activatedAt) : ''}
              </span>
            )}
            <a href="#/settings/due-diligence">Who reviews by parameter</a>
          </p>
        </div>
        <div className="prose-actions">
          {openDraft ? (
            <button
              type="button"
              className="primary"
              disabled={!canWrite || !dirty || busy}
              title={canWrite ? undefined : refusalReason('programme.write')}
              onClick={saveDraft}
              data-action="tprm.exception-authority.a02"
            >
              Save draft
            </button>
          ) : (
            <button
              type="button"
              className="primary"
              disabled={!canWrite || busy}
              title={canWrite ? undefined : refusalReason('programme.write')}
              onClick={startDraft}
              data-action="tprm.exception-authority.a01"
            >
              Draft a change
            </button>
          )}
        </div>
      </div>

      {note && (
        <p className="prose-line" role="status">
          <Status tone="ok">Done</Status>
          {note}
        </p>
      )}
      {problems.length > 0 && (
        <div className="form-errors" role="alert">
          <h3>Fix these before it can be put in force</h3>
          <ol>
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ol>
        </div>
      )}
      {actError && <ErrorBox error={actError} what="That change" verb="made" />}

      {active?.authority && (
        <section className="panel" aria-labelledby="ea-active-h">
          <h2 id="ea-active-h">In force</h2>
          <AuthorityTable doc={active.authority} deciders={deciders} />
        </section>
      )}

      {openDraft && draft && (
        <section className="panel" aria-labelledby="ea-draft-h">
          <div className="dd-draft-head">
            <h2 id="ea-draft-h">Draft version {openDraft.versionNo}</h2>
            <span className="prose-sub">Drafted by {personLabel(names, openDraft.createdBy)}</span>
          </div>
          <div className="dd-levels">
            {SEVERITIES.map((s) => {
              const lv = draft.levels.find((l) => l.severity === s.code)
              if (!lv) return null
              return (
                <fieldset key={s.code} className="dd-level" disabled={!canWrite}>
                  <legend>
                    <Status tone={severityTone(s.code)}>{s.name}</Status>
                  </legend>
                  <ol className="dd-steps">
                    {lv.steps.map((st, j) => (
                      <li key={j}>
                        <select
                          aria-label={s.name + ' approval ' + (j + 1)}
                          value={st.persona}
                          onChange={(e) =>
                            setLevel(s.code, (l) => {
                              l.steps[j] = { persona: e.target.value }
                            })
                          }
                        >
                          {choosable.map((d) => (
                            <option key={d.code} value={d.code}>
                              {d.name}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          aria-label={'Remove approval ' + (j + 1) + ' at ' + s.name}
                          disabled={lv.steps.length <= 1}
                          onClick={() =>
                            setLevel(s.code, (l) => {
                              l.steps.splice(j, 1)
                            })
                          }
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ol>
                  <div className="dd-level-foot">
                    <button
                      type="button"
                      disabled={lv.steps.length >= 3}
                      onClick={() =>
                        setLevel(s.code, (l) => {
                          const used = new Set(l.steps.map((x) => x.persona))
                          const next = choosable.find((d) => !used.has(d.code))
                          if (next) l.steps.push({ persona: next.code })
                        })
                      }
                    >
                      Add an approval
                    </button>
                    {lv.steps.length > 1 && (
                      <label className="dd-check">
                        <input
                          type="checkbox"
                          checked={lv.inOrder}
                          onChange={(e) =>
                            setLevel(s.code, (l) => {
                              l.inOrder = e.target.checked
                            })
                          }
                        />
                        In this order
                      </label>
                    )}
                  </div>
                </fieldset>
              )
            })}
          </div>
          <div className="prose-buttons">
            <button
              type="button"
              disabled={!canPromote || drewIt || dirty || busy}
              title={
                !canPromote
                  ? refusalReason('risk.approve')
                  : drewIt
                    ? 'You drafted this version, so a second person puts it in force.'
                    : dirty
                      ? 'Save the draft first.'
                      : undefined
              }
              onClick={promote}
              data-action="tprm.exception-authority.a03"
            >
              Put in force
            </button>
            {drewIt && (
              <Toggletip label="Why you cannot put it in force">
                You drafted this version, so a second person puts it in force.
              </Toggletip>
            )}
          </div>
        </section>
      )}

      <section className="panel panel-flush" aria-labelledby="ea-history-h">
        <div className="prose-panelhead">
          <h2 id="ea-history-h">Versions</h2>
        </div>
        <div className="table-wrap">
          <table className="dd-table">
            <caption className="vh">Every version of who can accept risks</caption>
            <thead>
              <tr>
                <th scope="col">Version</th>
                <th scope="col">Status</th>
                <th scope="col">Who</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.id}>
                  <th scope="row">v{v.versionNo}</th>
                  <td>
                    <Status tone={v.status === 'ACTIVE' ? 'ok' : v.status === 'DRAFT' ? 'brand' : 'plain'}>
                      {v.status === 'ACTIVE' ? 'In force' : v.status === 'DRAFT' ? 'Draft' : 'Retired'}
                    </Status>
                  </td>
                  <td className="dd-who">
                    <span>
                      Drafted by <b>{v.createdBy === 'seed' ? 'the shipped default' : personLabel(names, v.createdBy)}</b>
                    </span>
                    {v.activatedBy && (
                      <span>
                        In force by <b>{v.activatedBy === 'seed' ? 'the shipped default' : personLabel(names, v.activatedBy)}</b>
                        {v.activatedAt ? ', ' + shortDate(v.activatedAt) : ''}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}
